// The agent's home — "Panom".
//
// Deliberately NOT the period report (that is "Gelişimim" / AgentReport): this screen
// answers "what do I have to DO, and what is the newest thing that happened to me". So it
// has NO period picker, and it leads with the most recent call — its score, its recording
// (playable right here), and the objection button while the row is still `published`.
// The two screens used to render the same component, which read as a broken menu.
//
// Every number still comes from the server (Rule 1). The only thing decided in the browser
// is which of the fetched rows is the newest one and which action that status implies —
// and the action buttons only navigate; the server re-checks every one of them.

import * as api from '../api';
import type { AgentReport, EvaluationDetail, EvaluationList, PanelUser } from '../types';
import { useAsync } from '../hooks';
import {
  Callout, Empty, Icon, Loading, Note, PageHead, Panel, Pill, ScoreHero, ScorePill,
} from '../ui';
import { STATUS_LABELS, STATUS_TONE, fullDateTime, score10, shortDateTime } from '../format';
import { navigate } from '../App';

/** How many of the agent's own rows the feed asks for: one hero + a short "earlier" list. */
const FEED = 6;

export default function MyDashboard({ user }: { user: PanelUser }) {
  // One request set, one loading state, one error path — a home that renders half its
  // panels while the rest spin looks broken. `status` counts come from the list endpoint's
  // `total` (LIMIT 1), so the numbers are the server's, not a count of what we fetched.
  const state = useAsync<[EvaluationList, EvaluationList, EvaluationList, AgentReport, EvaluationDetail | null]>(
    async () => {
      const [feed, objectable, open, week] = await Promise.all([
        api.listEvaluations({ limit: FEED }),
        api.listEvaluations({ status: 'published', limit: 1 }),
        api.listEvaluations({ status: 'objected', limit: 1 }),
        api.getAgentReport('me'),
      ]);
      // One extra round-trip, and it earns it: the list row only carries a COUNT, and a call
      // may have been recorded across up to three files. The objection right hangs on the
      // agent hearing the whole call, so the hero panel plays all of them rather than the
      // first one with the rest hidden behind "Detayı aç".
      const newestRow = feed.evaluations[0] ?? null;
      const detail = newestRow ? await api.getEvaluation(newestRow.id) : null;
      return [feed, objectable, open, week, detail];
    },
    [],
  );

  const firstName = user.displayName.split(' ')[0];
  const brandName = state.data ? state.data[3].agent.brandName : '';
  const head = (
    <PageHead
      icon="dashboard"
      title={`Merhaba ${firstName} 👋`}
      subtitle={brandName ? `${brandName} · çağrı kayıtların, puanların ve itiraz hakkın` : ' '}
      right={(
        <button className="btn soft" type="button" onClick={() => navigate('report')}>
          <Icon name="insights" /><span>Gelişimim</span>
        </button>
      )}
    />
  );

  if (state.error) return <>{head}<Note error>{state.error}</Note></>;
  if (!state.data) return <>{head}<Loading /></>;

  const [feed, objectable, openObjections, week, newestDetail] = state.data;
  const newest = feed.evaluations[0] ?? null;
  const earlier = feed.evaluations.slice(1);
  // The detail is fetched for the newest row only, so a stale one (the feed reloaded, the
  // detail did not) would point players at another call's recordings.
  const newestAudios = newestDetail && newest && newestDetail.id === newest.id
    ? newestDetail.audios
    : [];

  // This week at a glance — one line, not a KPI wall: the wall is the report, and repeating
  // it here is exactly the duplication this screen exists to undo.
  const weekTitle = week.summary.evalCount
    ? `Bu hafta ${week.summary.evalCount} değerlendirme`
    : 'Bu hafta henüz değerlendirme yok';
  const rank = week.standing?.rank ?? null;
  const weekHint = week.summary.avgScore === null
    ? 'puan bekleniyor'
    : rank !== null
      ? `ortalama ${score10(week.summary.avgScore)}/10 · ${week.agent.brandName} ekibinde ${rank}/${week.standing?.of}`
      : `ortalama ${score10(week.summary.avgScore)}/10`;

  return (
    <>
      {head}

      <Callout tone={week.summary.criticalCount ? 'bad' : 'accent'} icon="insights">
        <b>{weekTitle}</b>
        {' · '}
        {weekHint}
      </Callout>

      <div className="grid-2 narrow" style={{ marginTop: 14 }}>
        <div className="stack">
          <Panel
            icon="graphic_eq"
            title="Son değerlendirmem"
            hint={newest ? fullDateTime(newest.callAt) : undefined}
            right={newest ? <Pill tone={STATUS_TONE[newest.status]}>{STATUS_LABELS[newest.status]}</Pill> : undefined}
          >
            {!newest ? (
              <>
                <Empty>Henüz değerlendirilmiş bir çağrın yok.</Empty>
                <div className="panel-body">
                  <Callout icon="info">
                    Çağrı kayıtlarını QMS ekibi yükler; ilk değerlendirmen yapıldığında burada görünür.
                  </Callout>
                </div>
              </>
            ) : (
              <>
                <div className="panel-body">
                  <ScoreHero
                    score={newest.finalScore}
                    hasCritical={newest.hasCritical}
                    label={newest.qualityLabel}
                  />
                  {newest.hasCritical ? (
                    <Callout tone="bad" icon="warning">
                      Kritik hata işaretlendi · bu çağrı 0,0 aldı.
                    </Callout>
                  ) : null}
                  <div className="callmeta" style={{ marginTop: 14 }}>
                    <div><div className="micro">Tarih</div><div className="v">{fullDateTime(newest.callAt)}</div></div>
                    {newest.playerUserId != null ? <div><div className="micro">Kullanıcı ID</div><div className="v" translate="no">{newest.playerUserId}</div></div> : null}
                    {newest.listName ? <div><div className="micro">Liste</div><div className="v">{newest.listName}</div></div> : null}
                    {newest.callType && newest.callType !== newest.listName
                      ? <div><div className="micro">Arama tipi</div><div className="v">{newest.callType}</div></div> : null}
                    <div><div className="micro">Değerlendiren</div><div className="v">{newest.evaluatorName}</div></div>
                  </div>
                </div>
                {/* The recordings are the whole point of the objection right: the agent has
                    to be able to hear the call before deciding — ALL of it, so a call split
                    across a transfer or a call-back gets one player per file. The API
                    answers Range requests and re-checks the row scope on every one of them.
                    A row scored under the pre-2026-09-07 rule may carry none — say that
                    rather than rendering a player that cannot play. */}
                {newestAudios.length ? newestAudios.map((a, i) => (
                  <div className="player" key={a.id}>
                    <Icon name="mic" style={{ color: 'var(--accent)' }} />
                    <audio controls preload="metadata" src={api.audioSrc(a.url)}>
                      Tarayıcınız ses oynatmayı desteklemiyor.
                    </audio>
                    <span className="meta">
                      {newestAudios.length > 1 ? `${i + 1}/${newestAudios.length} · ` : ''}
                      çağrı kaydı
                    </span>
                  </div>
                )) : (
                  <div className="player">
                    <Icon name="mic_off" style={{ color: 'var(--faint)' }} />
                    <span className="meta">Bu çağrı için kayıt yüklenmedi.</span>
                  </div>
                )}
                <div className="panel-foot">
                  <div className="row">
                    <button className="btn pri" type="button" onClick={() => navigate(`evaluation/${newest.id}`)}>
                      <Icon name="chevron_right" /><span>Detayı aç</span>
                    </button>
                    {newest.status === 'published' ? (
                      <button className="btn soft" type="button" onClick={() => navigate(`evaluation/${newest.id}`)}>
                        <Icon name="gavel" /><span>İtiraz et</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </Panel>

          {earlier.length ? (
            <Panel
              icon="history"
              title="Önceki değerlendirmelerim"
              right={<button className="link" type="button" onClick={() => navigate('evaluations')}>Tümü</button>}
            >
              <div className="panel-body">
                {earlier.map((e) => (
                  <div className="item" key={e.id}>
                    <div className="item-txt">
                      <b>{[e.listName, e.callType !== e.listName ? e.callType : ''].filter(Boolean).join(' · ') || '—'}</b>
                      <div className="item-hint">
                        {shortDateTime(e.callAt)}
                        {' · '}
                        {e.evaluatorName}
                      </div>
                    </div>
                    <div className="row">
                      <ScorePill score={e.finalScore} hasCritical={e.hasCritical} />
                      <Pill tone={STATUS_TONE[e.status]}>{STATUS_LABELS[e.status]}</Pill>
                      <button className="link" type="button" onClick={() => navigate(`evaluation/${e.id}`)}>
                        Detay
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}
        </div>

        <div className="stack">
          {/* The card is a summary; the full history (every appeal, reply and outcome) is the
              İtirazlarım screen (owner 2026-09-21). */}
          <Panel
            icon="gavel"
            title="İtiraz durumum"
            right={<button className="link" type="button" onClick={() => navigate('objections')}>Tümü</button>}
          >
            <div className="panel-body">
              <div className="item">
                <div className="item-txt">
                  İtiraz edebileceğin değerlendirme
                  <div className="item-hint">puanına katılmıyorsan her birine bir kez itiraz edebilirsin</div>
                </div>
                <div className="item-val">{objectable.total}</div>
              </div>
              <div className="item">
                <div className="item-txt">
                  Yanıt bekleyen itirazın
                  <div className="item-hint">QMS uzmanı inceliyor</div>
                </div>
                <div className="item-val">{openObjections.total}</div>
              </div>
              {openObjections.total > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <Callout tone="warn" icon="pending_actions">İtirazın QMS ekibinde · yanıt bekleniyor.</Callout>
                </div>
              ) : newest && newest.status === 'revised' ? (
                <div style={{ marginTop: 12 }}>
                  <Callout tone="good" icon="task_alt">İtirazın kabul edildi, puanın güncellendi.</Callout>
                </div>
              ) : newest && newest.status === 'upheld' ? (
                <div style={{ marginTop: 12 }}>
                  <Callout icon="rule">İtirazın incelendi, puan korundu.</Callout>
                </div>
              ) : null}
            </div>
          </Panel>

          {/* The panel that answers "where do I upload my call?" — nowhere: an agent never
              uploads. The recording enters the system with the evaluation, from the QMS
              side (POST /api/evaluations is gated to qms/admin), and this says so. */}
          <Panel icon="info" title="Sistem nasıl çalışıyor?">
            <div className="panel-body">
              <div className="item">
                <div className="item-txt">
                  <span className="w">1</span> QMS uzmanı, çağrı kaydını sisteme yükleyerek ve kriterler üzerinden puan verir.
                </div>
              </div>
              <div className="item">
                <div className="item-txt">
                  <span className="w">2</span> Değerlendirmen yayınlandığında burada görünür; arama kaydını da dinleyerek verilen puanı değerlendirebilirsin.
                </div>
              </div>
              <div className="item">
                <div className="item-txt">
                  <span className="w">3</span> Katılmadığın bir skora bir kez itiraz edebilirsin; Supervisor kontrol ederek gerekirse tekrar puan verir.
                </div>
              </div>
              <div className="item">
                <div className="item-txt">
                  <span className="w">4</span> Çağrında kritik hata olduğunda otomatik 0 puan alırsın. (Verilen diğer puanlar iyi olsa da)
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Callout icon="info">Çağrı kayıtlarını agent yüklemez — kayıtları sisteme QMS ekibi ekler.</Callout>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
